/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `openAppointmentRequest` for the Caintra Connect frontend.
 * @module logic/appointments/openAppointmentRequest
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { getAppointmentDetail, updateAppointment } from "../appointment/index.js";
import { markAppointmentAsSeenInCollections } from "./appointmentStateUpdates.js";

/**
 * Loads appointment detail, marks unseen requests as seen, and returns navigation state.
 */
export const prepareAppointmentRequestNavigation = async ({
  navigate,
  request,
  eventData,
  appointments,
  participatingCompanyData,
  setters,
}) => {
  const detail = await getAppointmentDetail(
    navigate,
    request._id,
    eventData._id,
  );
  const requestForDetail = detail?.appointment;

  if (!requestForDetail?._id) {
    return null;
  }

  if (requestForDetail.estado === "notSeen") {
    const updatedAppointment = await updateAppointment(
      navigate,
      request._id,
      eventData._id,
      { appointmentInformation: { estado: "seen" } },
    );

    if (updatedAppointment) {
      requestForDetail.estado = "seen";
      markAppointmentAsSeenInCollections(request._id, setters);
    }
  }

  return {
    request: requestForDetail,
    requirement: detail.requirement,
    user: detail.senderUser,
    participatingCompany:
      detail.participatingCompany || participatingCompanyData,
    navigationState: {
      request: requestForDetail,
      requirement: detail.requirement,
      appointments,
      eventData,
      user: detail.senderUser,
      participatingCompanyData:
        detail.participatingCompany || participatingCompanyData,
    },
  };
};
